import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/api/client";
import { ArrowRight, ChevronRight, Trophy } from "lucide-react";
import { PublicSearchTrigger } from "@/components/PublicAlgoliaSearch";

const TXT = {
  en: {
    badge: "Official Help Center",
    headline1: "How Can We",
    headline2: "Help You?",
    description: "Everything you need — articles, guides and answers.",
    placeholder: "Search across all topics…",
    products: "Topics",
    chooseProduct: "Choose Your Topic",
    chooseSub: "Topics. Hundreds of answers. One support team.",
    categories: "Categories",
    browseByTopic: "Browse by Topic",
    browseSub: "Jump directly to the section you need",
    noArticlesYet: "No articles yet.",
    loadingArticles: "Loading articles…",
    mostSearched: "Most Searched",
    popular: "Popular Articles",
    sections: "sections",
    articles: "articles",
    article: "article",
    questions: "questions",
    products_label: "Topics",
    aiSupport: "AI Support",
    stillHaveQuestions: "Still have questions?",
    contactSupport: "Contact Support",
    contactCta: "Our team is ready to help — fast, friendly, and to the point.",
  },
  ar: {
    badge: "مركز المساعدة الرسمي",
    headline1: "كيف يمكننا",
    headline2: "مساعدتك؟",
    description: "كل ما تحتاجه — مقالات وأدلة وإجابات.",
    placeholder: "ابحث في كل المواضيع…",
    products: "المواضيع",
    chooseProduct: "اختر موضوعك",
    chooseSub: "مواضيع. مئات الإجابات. فريق دعم واحد.",
    categories: "الفئات",
    browseByTopic: "تصفح حسب الموضوع",
    browseSub: "انتقل مباشرة إلى القسم الذي تحتاجه",
    noArticlesYet: "لا توجد مقالات بعد.",
    loadingArticles: "جاري تحميل المقالات…",
    mostSearched: "الأكثر بحثاً",
    popular: "المقالات الشائعة",
    sections: "أقسام",
    articles: "مقالات",
    article: "مقال",
    questions: "أسئلة",
    products_label: "مواضيع",
    aiSupport: "دعم AI",
    stillHaveQuestions: "هل لديك أسئلة أخرى؟",
    contactSupport: "تواصل مع الدعم",
    contactCta: "فريقنا جاهز للمساعدة — بسرعة وبشكل ودود.",
  },
};

// per-topic accent palette
const ACCENTS = ["#41B5E6", "#A855F7", "#22C55E", "#F59E0B", "#EC4899", "#06B6D4"];

export default function PublicHomePage() {
  const { space, slug, basePath = `/s/${slug}`, lang, supportEmail, primary, openPublicSearch } = useOutletContext();
  const t = TXT[lang || "en"];
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [tabArticles, setTabArticles] = useState({});
  const [loadingTabArticles, setLoadingTabArticles] = useState(false);

  useEffect(() => {
    api.get(`/public/categories?space=${encodeURIComponent(slug)}&lang=${lang}`).then((r) => {
      setCategories(r.data);
      const topLevel = r.data.filter((c) => !c.parent_id);
      if (topLevel.length) setActiveTab(topLevel[0].id);
    });
    setTabArticles({});
    api.get(`/public/popular?space=${encodeURIComponent(slug)}&limit=6&lang=${lang}`).then((r) => setPopular(r.data));
    api.post(`/public/analytics/event?space=${encodeURIComponent(slug)}`, {
      event_type: "page_view",
      visitor_id: localStorage.getItem("visitor_id") || "",
    }).catch(() => {});
  }, [slug, lang]);

  useEffect(() => {
    if (!activeTab) return;
    if (Object.prototype.hasOwnProperty.call(tabArticles, activeTab)) return;
    const topic = categories.find((c) => c.id === activeTab);
    if (!topic) return;

    setLoadingTabArticles(true);
    api.get(`/public/topics/${topic.slug}?space=${encodeURIComponent(slug)}&lang=${lang}`).then((r) => {
      const articles = (r.data.sections || []).flatMap((section) =>
        (section.articles || []).map((article) => ({
          ...article,
          sectionName: section.name,
          sectionIcon: section.icon,
        }))
      );
      setTabArticles((prev) => ({ ...prev, [activeTab]: articles }));
    }).catch(() => {
      setTabArticles((prev) => ({ ...prev, [activeTab]: [] }));
    }).finally(() => setLoadingTabArticles(false));
  }, [activeTab, slug, categories, lang, tabArticles]);

  const topLevel = categories.filter((c) => !c.parent_id);
  const totalArticles = categories.reduce((sum, c) => sum + (c.article_count || 0), 0);
  const activeArticles = tabArticles[activeTab] || [];

  return (
    <div className="bg-[var(--pp-bg)]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Background glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "30%", left: "50%", transform: "translate(-50%, -30%)",
            width: "900px", height: "900px",
            background: `radial-gradient(circle, ${primary}25 0%, transparent 60%)`,
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-20 sm:pt-28 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-10 text-[11px] uppercase tracking-[0.25em] font-bold rounded-full border bg-[var(--pp-surface)]"
            style={{ borderColor: `${primary}55`, color: primary, boxShadow: `0 0 24px ${primary}25` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: primary, boxShadow: `0 0 8px ${primary}` }} />
            {t.badge} — {space.name}
          </div>

          <h1 className="font-display text-6xl sm:text-8xl md:text-9xl leading-[0.92] mb-8 tracking-tight">
            <span className="text-[var(--pp-text)] block">{t.headline1}</span>
            <span className="block glow-accent-blue" style={{ color: primary }}>{t.headline2}</span>
          </h1>

          <p className="text-base sm:text-lg text-[var(--pp-text-muted)] mb-12 max-w-2xl mx-auto">
            {space.description || t.description}
          </p>

          {/* Search with dropdown */}
          {space.settings?.show_search !== false && (
            <div className="max-w-2xl mx-auto mb-10">
              <PublicSearchTrigger
                placeholder={t.placeholder}
                testId="hero-search-input"
                onOpen={openPublicSearch}
                iconClassName="w-5 h-5"
                className="[&_svg]:ltr:!left-5 [&_svg]:rtl:!right-5"
                inputClassName="py-4 ltr:pl-14 ltr:pr-5 rtl:pr-14 rtl:pl-5 text-base font-medium text-[var(--pp-text-dim)] hover:border-[var(--pp-border-hover)] focus:border-[var(--pp-accent)] transition-all"
              />
            </div>
          )}

          {/* Topic pills row */}
          {topLevel.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-w-2xl mx-auto">
              {topLevel.slice(0, 6).map((c, i) => {
                const accent = ACCENTS[i % ACCENTS.length];
                return (
                  <Link key={c.id} to={`${basePath}/c/${c.slug}`}
                    data-testid={`pill-${c.slug}`}
                    className="group flex items-center gap-2.5 px-4 py-2.5 bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[color:var(--accent)] rounded-full text-sm font-semibold transition-all"
                    style={{ "--accent": accent }}>
                    <span className="text-base">{c.icon || "📁"}</span>
                    <span className="text-[var(--pp-text)] text-[13px] uppercase tracking-wider font-bold flex-1 text-left">{c.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats strip */}
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-14">
          <div className="bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-2xl"
            style={{ boxShadow: `0 0 1px ${primary}33, inset 0 0 60px rgba(0,0,0,0.6)` }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[var(--pp-border)] rtl:divide-x-reverse">
              <Stat value={topLevel.length} label={t.products_label} accent={primary} />
              <Stat value={categories.filter((c) => c.parent_id).length} label={t.categories} accent={primary} />
              <Stat value={`${totalArticles}+`} label={t.articles} accent={primary} />
              <Stat value="24/7" label={t.aiSupport} accent={primary} />
            </div>
          </div>
        </div>
      </section>

      {/* TOPIC CARDS */}
      <section className="py-16 sm:py-24 border-t border-[var(--pp-border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionLabel label={t.products} accent={primary} />
          <h2 className="font-display text-5xl sm:text-6xl mb-3 text-[var(--pp-text)]">{t.chooseProduct}</h2>
          <p className="text-[var(--pp-text-muted)] text-base mb-14">{t.chooseSub}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-categories-grid">
            {topLevel.map((c, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <Link key={c.id} to={`${basePath}/c/${c.slug}`}
                  data-testid={`public-category-${c.slug}`}
                  className="group relative bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[color:var(--accent)] rounded-2xl p-6 transition-all overflow-hidden"
                  style={{ "--accent": accent }}>
                  {/* Top edge accent line on hover */}
                  <div className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, boxShadow: `0 0 12px ${accent}88` }} />

                  {/* Icon tile */}
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-3xl border border-[var(--pp-border)]"
                    style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}08)` }}>
                    {c.icon || "📁"}
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.25em] font-black mb-2" style={{ color: accent }}>
                    {c.description ? c.description.slice(0, 36).toUpperCase().split(".")[0] : t.products}
                  </div>

                  <h3 className="font-semibold text-xl mb-2 text-[var(--pp-text)]" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.01em" }}>
                    {c.name}
                  </h3>

                  {c.description && <p className="text-sm text-[var(--pp-text-muted)] mb-6 line-clamp-2 leading-relaxed">{c.description}</p>}

                  <div className="pt-4 border-t border-[var(--pp-border)] flex items-center justify-between text-xs">
                    <span className="font-bold text-[var(--pp-text-dim)] uppercase tracking-wider text-[10px]">
                      {c.article_count} {t.articles}
                    </span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" style={{ color: accent }} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BROWSE BY TOPIC — Tabs */}
      {topLevel.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-[var(--pp-border)]" data-testid="browse-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionLabel label={t.categories} accent={primary} />
            <h2 className="font-display text-5xl sm:text-6xl mb-3 text-[var(--pp-text)]">{t.browseByTopic}</h2>
            <p className="text-[var(--pp-text-muted)] text-base mb-10">{t.browseSub}</p>

            {/* Tab strip */}
            <div className="bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-2xl p-1.5 mb-6 flex flex-wrap gap-1.5 overflow-x-auto">
              {topLevel.map((c, i) => {
                const accent = ACCENTS[i % ACCENTS.length];
                const isActive = activeTab === c.id;
                return (
                  <button key={c.id} onClick={() => setActiveTab(c.id)}
                    data-testid={`tab-${c.slug}`}
                    className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-black rounded-xl transition-all"
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${accent}, ${accent}dd)`,
                      boxShadow: `0 0 24px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.2)`,
                      color: "#fff",
                    } : { color: "var(--pp-text-muted)" }}>
                    <span>{c.icon}</span>{c.name}
                  </button>
                );
              })}
            </div>

            {/* Article cards for selected topic */}
            {loadingTabArticles ? (
              <div className="text-[var(--pp-text-muted)] text-center py-12 text-sm">{t.loadingArticles}</div>
            ) : activeArticles.length === 0 ? (
              <div className="text-[var(--pp-text-muted)] text-center py-12 text-sm">{t.noArticlesYet}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeArticles.map((a, i) => {
                  const accent = ACCENTS[topLevel.findIndex((c) => c.id === activeTab) % ACCENTS.length];
                  return (
                    <Link key={a.id} to={`${basePath}/a/${a.slug}`}
                      data-testid={`browse-article-${a.slug}`}
                      className="group bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[color:var(--accent)] rounded-xl p-5 transition-all"
                      style={{ "--accent": accent }}>
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg border border-[var(--pp-border)] flex-shrink-0"
                          style={{ background: `${accent}15` }}>
                          {a.sectionIcon || a.icon || "📄"}
                        </div>
                        <h3 className="font-semibold text-[15px] leading-snug text-[var(--pp-text)] line-clamp-2"
                          style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.01em" }}>
                          {a.title}
                        </h3>
                      </div>
                      <div className="pt-3 border-t border-[var(--pp-border)] flex items-center justify-between">
                        {a.sectionName ? (
                          <span className="text-[9px] uppercase tracking-[0.2em] font-black" style={{ color: accent }}>
                            {a.sectionName}
                          </span>
                        ) : <span />}
                        <ArrowRight className="w-3.5 h-3.5 text-[var(--pp-text-dim)] group-hover:text-[var(--pp-text)] group-hover:translate-x-0.5 transition-all rtl:rotate-180" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* POPULAR ARTICLES */}
      {popular.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-[var(--pp-border)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionLabel label={t.mostSearched} accent={primary} />
            <h2 className="font-display text-5xl sm:text-6xl mb-10 text-[var(--pp-text)]">{t.popular}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {popular.map((a, i) => {
                const accent = ACCENTS[i % ACCENTS.length];
                return (
                  <Link key={a.id} to={`${basePath}/a/${a.slug}`}
                    data-testid={`popular-${a.slug}`}
                    className="group bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[color:var(--accent)] rounded-xl p-5 transition-all"
                    style={{ "--accent": accent }}>
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg border border-[var(--pp-border)] flex-shrink-0"
                        style={{ background: `${accent}15` }}>
                        {a.topic?.icon || "📄"}
                      </div>
                      <h3 className="font-semibold text-[15px] leading-snug text-[var(--pp-text)] line-clamp-2"
                        style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.01em" }}>
                        {a.title}
                      </h3>
                    </div>
                    <div className="pt-3 border-t border-[var(--pp-border)] flex items-center justify-between">
                      {a.topic ? (
                        <span className="text-[9px] uppercase tracking-[0.2em] font-black" style={{ color: accent }}>
                          {a.topic.name}
                        </span>
                      ) : <span />}
                      <ArrowRight className="w-3.5 h-3.5 text-[var(--pp-text-dim)] group-hover:text-[var(--pp-text)] group-hover:translate-x-0.5 transition-all rtl:rotate-180" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* STILL HAVE QUESTIONS */}
      <section className="py-20 border-t border-[var(--pp-border)] relative overflow-hidden">
        <div
          className="absolute pointer-events-none"
          style={{
            top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "600px", height: "300px",
            background: `radial-gradient(ellipse, ${primary}20 0%, transparent 70%)`,
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <h3 className="font-display text-4xl sm:text-5xl mb-4 text-[var(--pp-text)]">{t.stillHaveQuestions}</h3>
          <p className="text-[var(--pp-text-muted)] mb-8">{t.contactCta}</p>
          <a href={`mailto:${supportEmail}`} data-testid="contact-email-btn"
            className="inline-flex items-center gap-2 px-7 py-3.5 text-[var(--pp-text)] font-black text-xs uppercase tracking-[0.25em] rounded-full hover:opacity-90 hover:scale-[1.02] transition-all"
            style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)`, boxShadow: `0 0 32px ${primary}55` }}>
            📧 {t.contactSupport} — {supportEmail}
          </a>
        </div>
      </section>
    </div>
  );
}

function SectionLabel({ label, accent }) {
  return (
    <div className="flex items-center gap-4 mb-4">
      <span className="text-[10px] uppercase tracking-[0.3em] font-black" style={{ color: accent }}>{label}</span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
    </div>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div className="py-6 px-3 text-center">
      <div className="font-display text-4xl tracking-tight" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-black text-[var(--pp-text-muted)] mt-1">{label}</div>
    </div>
  );
}
